import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { cardFrame, typeOnDurationMs } from './cardGeometry';
import './dossier.css';

// Under the page's base path: the build may be served from /client/, not the site root.
const MARK_URL = `${import.meta.env.BASE_URL}mark-alpha.png`;
const CARD_WIDTH = 552;

export interface DossierCardProps {
  /**
   * The step's authored display name (SessionLine.stepTitle, or a config-node
   * fallback), shown as the grey status word. Omit it (not the empty
   * string) to show the mark alone.
   *
   * Ruling, Jason, 2026-09-28: "o¿o is Throckmorton" — the mark is his
   * identity, not a logo beside his printed name, so this is never a
   * speaker's name. See docs/glasses-client/10-server-directed-display-design.md.
   */
  status?: string;
  /** The line to type on. */
  body: string;
  /** Mark this line as speech in progress; reveal timing starts at playback. */
  reveal?: boolean;
  /** Actual browser playback state for pacing a spoken line's visual reveal. */
  playbackStarted?: boolean;
  playbackDurationMs?: number | null;
  playbackComplete?: boolean;
  /**
   * 'line' (default): the ordinary waiting-line card, with the o¿o mark and,
   * when a step title is known, the status word. 'status': a
   * presentation-directive card (display.text/display.target) — same frame
   * and typed body, no header at all (no mark, no status word), per the
   * style guide's "no mark, no header word" rule for these cards.
   */
  variant?: 'line' | 'status';
}

/**
 * The flat notched dossier card from the pitch clips (Untracked frame 188):
 * a dark glass panel, a lit left edge in two-stage neon, unlit thin edges, an
 * inset hairline, the o¿o mark (Throckmorton's identity, never a name beside
 * it) and, when a step title is known, a grey status word over a rule; the
 * body typed on once.
 *
 * presence.show is intentionally not wired to any reveal animation here: the
 * app already has an o¿o mark reveal in the sting (Sting.tsx,
 * stingTimeline.ts). TODO(Jason): decide whether presence.show reuses a
 * shortened/header-scale variant of that sting instead of new motion here.
 */
export function DossierCard({ status, body, reveal = false, playbackStarted = false, playbackDurationMs, playbackComplete = false, variant = 'line' }: DossierCardProps) {
  const cardRef = useRef<HTMLElement>(null);
  const [height, setHeight] = useState(0);
  const typed = useTypeOn(body, reveal, playbackStarted, playbackDurationMs, playbackComplete);

  useLayoutEffect(() => {
    const node = cardRef.current;
    if (!node) return;
    // Layout height stays in stage coordinates when the phone scales its parent.
    const measure = () => setHeight(node.offsetHeight);
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const frame = height > 0 ? cardFrame(CARD_WIDTH, height) : null;

  return (
    <section
      className={variant === 'status' ? 'dossier-card dossier-card-status' : 'dossier-card'}
      ref={cardRef}
      style={{ width: CARD_WIDTH }}
      aria-label={variant === 'status' ? 'Cue' : status ?? 'Throckmorton'}
    >
      {frame && (
        <svg
          className="dossier-frame"
          width={CARD_WIDTH}
          height={height}
          viewBox={`0 0 ${CARD_WIDTH} ${height}`}
          aria-hidden="true"
        >
          <defs>
            <filter id="dossier-neon" x="-20%" y="-20%" width="140%" height="140%">
              <feGaussianBlur in="SourceGraphic" stdDeviation="2" result="tight" />
              <feGaussianBlur in="SourceGraphic" stdDeviation="10" result="wide" />
              <feColorMatrix
                in="wide"
                type="matrix"
                values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 0.55 0"
                result="wideDim"
              />
              <feMerge>
                <feMergeNode in="wideDim" />
                <feMergeNode in="tight" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
            <linearGradient id="dossier-bleed" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0" stopColor="#1E404A" />
              <stop offset="0.12" stopColor="#172C36" />
              <stop offset="0.5" stopColor="#13212B" />
              <stop offset="1" stopColor="#111F28" />
            </linearGradient>
            <linearGradient id="dossier-sheen" x1="0" y1="0" x2="0.6" y2="1">
              <stop offset="0" stopColor="#ffffff" stopOpacity="0.06" />
              <stop offset="0.5" stopColor="#ffffff" stopOpacity="0" />
            </linearGradient>
          </defs>
          <path d={frame.outline} fill="url(#dossier-bleed)" />
          <path d={frame.outline} fill="url(#dossier-sheen)" />
          <path d={frame.hairline} fill="none" stroke="#4E8088" strokeWidth={2.5} />
          <g fill="none" stroke="#4A7A82" strokeWidth={8}>
            {frame.unlit.map((d) => <path key={d} d={d} />)}
          </g>
          <g fill="none" stroke="#B2FAFF" strokeWidth={16} strokeLinejoin="miter" filter="url(#dossier-neon)">
            {frame.lit.map((d) => <path key={d} d={d} />)}
          </g>
        </svg>
      )}
      <div className="dossier-content">
        {variant === 'line' && (
          <>
            <header className="dossier-header">
              <img className="dossier-mark" src={MARK_URL} alt="" />
              {status ? (
                <>
                  <span className="dossier-dot" />
                  <span className="dossier-status">{status}</span>
                </>
              ) : null}
            </header>
            <div className="dossier-rule">
              <span className="dossier-rule-lit" />
            </div>
          </>
        )}
        <p className="dossier-body">
          <span className="dossier-bullet" aria-hidden="true" />
          {/* Readers and tests get the whole line at once; the typed copy is visual only.
              The untyped remainder keeps its space so the card does not grow as it types. */}
          {typed.running ? (
            <>
              <span className="dossier-visually-hidden">{body}</span>
              <span className="dossier-body-text" aria-hidden="true">
                <span className="dossier-body-typed">{typed.text}</span>
                <span className="dossier-cursor" />
                <span className="dossier-body-rest">{body.slice(typed.text.length)}</span>
              </span>
            </>
          ) : (
            <span className="dossier-body-text">{body}</span>
          )}
        </p>
      </div>
    </section>
  );
}

/** Reveals text once, at the style guide's rate. No loop, no blinking. */
function useTypeOn(text: string, reveal: boolean, playbackStarted: boolean, playbackDurationMs: number | null | undefined, playbackComplete: boolean): { text: string; running: boolean } {
  const revealedText = useRef<string | null>(null);
  // The line whose reveal was paced to playing audio; if that pacing ends for any
  // reason (the audio finished, was cut off) the line counts as fully revealed.
  const pacedText = useRef<string | null>(null);
  const [shown, setShown] = useState(() => (prefersReducedMotion() ? text.length : 0));

  useEffect(() => {
    if (reveal && playbackStarted) pacedText.current = text;
    else if (pacedText.current === text) {
      pacedText.current = null;
      revealedText.current = text;
      setShown(text.length);
      return;
    }
    if (playbackComplete) {
      revealedText.current = text;
      setShown(text.length);
      return;
    }
    if (revealedText.current === text) {
      setShown(text.length);
      return;
    }
    if (prefersReducedMotion()) {
      setShown(text.length);
      return;
    }
    if (reveal && !playbackStarted) {
      setShown(0);
      return;
    }
    const duration = playbackStarted && playbackDurationMs && Number.isFinite(playbackDurationMs) && playbackDurationMs > 0
      ? playbackDurationMs
      : typeOnDurationMs(text);
    if (duration === 0) {
      setShown(text.length);
      return;
    }
    setShown(0);
    const start = performance.now();
    let frame = 0;
    const tick = (now: number) => {
      const count = Math.min(text.length, Math.ceil(((now - start) / duration) * text.length));
      setShown(count);
      if (count < text.length) frame = requestAnimationFrame(tick);
      // Remember a finished reveal: when the spoken-line state clears at the
      // end of playback this effect re-runs, and without this the line would
      // be typed out a second time.
      else revealedText.current = text;
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [text, reveal, playbackStarted, playbackDurationMs, playbackComplete]);

  return playbackComplete || revealedText.current === text
    ? { text, running: false }
    : { text: text.slice(0, shown), running: shown < text.length };
}

function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}
